import { AxiosPromise, AxiosRequestConfig } from 'axios'
import { UserRole } from '../enums/UserRole'
import { AlertNotifications } from '../types/Alerts'
import {
  User,
  UserQuery,
  UserInput,
  ProjectMember,
  ActionRequest,
  OtpInput,
  SendEmailFromInstaller,
  UpdateUserInput,
  UserAlertRule,
  UserAlertRuleQuery,
  SignupCompany
} from '../types'
import createAPIClient from '../services/apiClient'
import getAxios from './axios'
import UserAlertRuleInput from '../types/UserAlertRuleInput'

const api = createAPIClient('/v2')

const axios = getAxios()

export async function getUsers(query?: UserQuery): Promise<User[]> {
  const response = await api.get(`/users`, {
    params: {
      showDeleted: query?.showDeleted ?? false
    }
  })

  return response.data
}

export async function getUser(id: number, showDeleted = false): Promise<User> {
  const response = await api.get(`/users/${id}`, {
    params: {
      showDeleted: showDeleted
    }
  })

  return response.data
}

export async function getProjectMembers(id: number): Promise<ProjectMember[]> {
  const response = await api.get(`/projects/${id}/members`)

  return response.data
}

export async function createUser(input: UserInput): Promise<User> {
  const response = await api.post('/users', input)

  return response.data
}

export async function updateUser(id: number, input: UpdateUserInput): Promise<User> {
  const response = await api.put(`/users/${id}`, input)

  return response.data
}

export interface UpdateUserPhone {
  phone: string
}

export async function updateUserPhone(
  id: number,
  input: UpdateUserPhone,
  config: AxiosRequestConfig = {}
): Promise<User> {
  const response = await api.patch(`users/${id}/phone`, input, config)

  return response.data
}

export interface UpdateUserEmail {
  email: string
}

export async function updateUserEmail(
  id: number,
  input: UpdateUserEmail,
  config: AxiosRequestConfig = {}
): Promise<User> {
  const response = await api.patch(`users/${id}/email`, input, config)

  return response.data
}

export async function deleteUser(id: number, permanently = false): Promise<void> {
  await api.delete(`/users/${id}`, {
    params: {
      permanently
    }
  })
}

export async function recoverUser(id: number): Promise<void> {
  await api.patch(`/users/${id}/recover`)
}

export interface ManufacturerInvite {
  email: string
  role: UserRole
}

export async function inviteManufacturer(email: ManufacturerInvite): Promise<void> {
  await api.post('/users/invite-manufacturer', email)
}

export async function sendSendEmailFromInstaller(
  input: SendEmailFromInstaller
): Promise<SendEmailFromInstaller> {
  const response = await api.post('/users/email-from-installer', input)

  return response.data
}

export async function getUserAlertRules(
  id: number,
  query?: UserAlertRuleQuery,
  config?: AxiosRequestConfig
): Promise<UserAlertRule[]> {
  const response = await api.get(`/users/${id}/alertrules`, {
    params: query,
    ...config
  })

  return response.data
}

export async function getUserAlertRulesByProject(
  id: number,
  projectId: number,
  config?: AxiosRequestConfig
): Promise<UserAlertRule[]> {
  const response = await api.get(`/users/${id}/alertrules/project/${projectId}`, config)
  return response.data
}

export async function getUserAlertRulesByDevice(
  id: number,
  deviceName: string,
  config?: AxiosRequestConfig
): Promise<UserAlertRule[]> {
  const response = await api.get(`/users/${id}/alertrules/device/${deviceName}`, config)
  return response.data
}

export async function getUserAlertRulesByRuuviTag(
  id: number,
  ruuviTagId: string,
  config?: AxiosRequestConfig
): Promise<UserAlertRule[]> {
  const response = await api.get(`/users/${id}/alertrules/ruuvi-tag/${ruuviTagId}`, config)
  return response.data
}

export async function updateUserAlertRules(
  id: number,
  input: UserAlertRuleInput[]
): Promise<UserAlertRule[]> {
  const response = await api.put(`/users/${id}/alertrules`, input)
  return response.data
}

export async function updateUserAlertRulesByProject(
  id: number,
  projectId: string | number,
  input: UserAlertRuleInput[]
): Promise<UserAlertRule[]> {
  const response = await api.put(`/users/${id}/alertrules/project/${projectId}`, input)

  return response.data
}

export async function updateUserAlertRulesByDevice(
  id: number,
  deviceName: string,
  input: UserAlertRuleInput[],
  config?: AxiosRequestConfig
): Promise<UserAlertRule[]> {
  const response = await api.put(`/users/${id}/alertrules/device/${deviceName}`, input, config)

  return response.data
}

export async function updateUserAlertRulesByRuuviTag(
  id: number,
  ruuviTagId: string,
  input: UserAlertRuleInput[],
  config?: AxiosRequestConfig
): Promise<UserAlertRule[]> {
  const response = await api.put(`/users/${id}/alertrules/ruuvi-tag/${ruuviTagId}`, input, config)

  return response.data
}

// TODO: Use different endpoint instead of different method for batch update.
// Eg. PATCH /users/:id/alertrules/toggle?projectId=:projectId
export async function toggleUserAlertRulesByProject(
  userId: number,
  projectId: number,
  input: AlertNotifications
): Promise<AxiosPromise> {
  const response = await api.patch(`/users/${userId}/alertrules/project/${projectId}`, input)

  return response.data
}

export async function getUserAlertRule(id: number, ruleId: string): Promise<UserAlertRule> {
  const response = await api.get(`/users/${id}/alertrules/${ruleId}`)

  return response.data
}

// FIXME: Add type "UserAlertRuleInput".
export async function updateUserAlertRule(
  userId: number,
  userAlertRuleId: number,
  input: UserAlertRuleInput
): Promise<UserAlertRule> {
  const response = await api.put(`/users/${userId}/alertrules/${userAlertRuleId}`, input)

  return response.data
}

// TODO: DELETE /users/:id/alertrules
export async function deleteUserAlertRule(id: number): Promise<void> {
  await api.delete(`/users/alertrules/${id}`)
}

export async function getActionRequestsOfUser(id: number): Promise<ActionRequest[]> {
  const response = await api.get(`users/${id}/action-requests`)

  return response.data
}

export async function sendOtpToEmail(input?: OtpInput): Promise<void> {
  //  If input is provided, user is setting up mfa. If not, user is trying to login.
  await axios.post(`users/otp-to-email`, input)
}

export async function disableMfa(id: string): Promise<void> {
  await axios.post(`users/${id}/disable-mfa`)
}

export async function getMe(): Promise<User> {
  const response = await api.get(`me`)

  return response.data
}

export async function getSignupCompanies(config?: AxiosRequestConfig): Promise<SignupCompany[]> {
  const response = await api.get(`me/signup-companies`, config)

  return response.data
}

export default {
  getUsers,
  getUser,
  createUser,
  updateUser,
  updateUserPhone,
  deleteUser,
  getProjectMembers,
  inviteManufacturer,
  sendSendEmailFromInstaller,
  getUserAlertRules,
  getUserAlertRulesByProject,
  getUserAlertRulesByDevice,
  getUserAlertRulesByRuuviTag,
  sendOtpToEmail,
  getActionRequestsOfUser,
  deleteUserAlertRule,
  getSignupCompanies
}
